/**
 * IOC (Indicator of Compromise) types and interfaces for OnFraud Blackbox API
 */

export type IOCType = 'domain' | 'ip' | 'url' | 'hash_md5' | 'hash_sha1' | 'hash_sha256';

export interface IOCMetadata {
    created_at: string;
    updated_at: string;
    ttl: string;
    raw_data: any;
    processing_notes: string[];
    validation_status: string | null;
    false_positive: boolean;
}

export interface IOC {
    id: string;
    value: string;
    type: IOCType;
    source: string;
    first_seen: string;
    last_seen: string;
    tags: string[];
    threat_types: string[];
    confidence: number;
    priority: number;
    metadata: IOCMetadata;
    ioc_id: string;
    ioc_value: string;
    ioc_type: IOCType;
}

export interface IOCLookupResponse {
    total: number;
    iocs: IOC[];
    error?: {
        status: number;
        message: string;
        isApiKeyExpired?: boolean;
    };
}

export interface IOCTypesResponse {
    types: IOCType[];
}

/**
 * Helper function to get severity level from IOC tags
 */
export function getIOCSeverity(ioc: IOC): 'critical' | 'high' | 'medium' | 'low' {
    const severityTags = ioc.tags.filter(tag => tag.startsWith('severity:'));
    if (severityTags.length > 0) {
        const severity = severityTags[0].split(':')[1];
        return severity as 'critical' | 'high' | 'medium' | 'low';
    }

    // Fallback to confidence/priority
    if (ioc.confidence >= 90 && ioc.priority >= 8) return 'critical';
    if (ioc.confidence >= 70 && ioc.priority >= 6) return 'high';
    if (ioc.confidence >= 50 && ioc.priority >= 4) return 'medium';
    return 'low';
}

/**
 * Helper function to get color for severity
 */
export function getSeverityColor(severity: 'critical' | 'high' | 'medium' | 'low'): string {
    switch (severity) {
        case 'critical': return 'error';
        case 'high': return 'warn';
        case 'medium': return 'accent';
        case 'low': return 'primary';
        default: return 'primary';
    }
}

/**
 * Helper function to get display label for severity
 */
export function getSeverityLabel(severity: 'critical' | 'high' | 'medium' | 'low'): string {
    switch (severity) {
        case 'critical': return 'Crítico';
        case 'high': return 'Alto';
        case 'medium': return 'Medio';
        case 'low': return 'Bajo';
        default: return 'Desconocido';
    }
}
